import React, { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { BrandLogoIcon, CopyIcon } from '@8x8/oxygen-icon'
import Select from '@8x8/oxygen-select'
import TextLink from '@8x8/oxygen-text-link'
import { IconButton } from '@8x8/oxygen-button'

import { AppContainer } from '~/components/app-container'
import { SessionWrapper } from '~/components/session-wrapper'
import useWindowSize from '~/components/hooks/use-window-size'
import withThemeAndStoreHoC from '~/components/with-theme-and-store'
import ActiveParticipantsSettings from '~/components/room-configuration/active-participants-settings'
import RecordingSettings from '~/components/room-configuration/recording-settings'
import AccessSettings from '~/components/room-configuration/access-settings'
import ModerationSettings from '~/components/room-configuration/moderation-settings'
import { Card } from '~/components/recents/session-details/styled'
import { PresetTitle, SettingsContainer, SettingsDescription, SettingsWrapper, SpinnerContainer, StyledLegend } from '~/components/room-configuration/components'
import Spinner from '~/components/spinner'
import SecuritySettings from '~/components/room-configuration/security-settings'
import MeetingPresetOption from '~/components/room-configuration/meeting-preset-option'
import withNotificationsHoC from '~/components/with-notifications'
import { showInlineNotification } from '~/core/ui'
import { translate } from '~/core/translate'
import DialogsRenderer from '~/components/dialogs-renderer'
import {
  LARGE_LAYOUT_THRESHOLD,
  MEETING_SETTINGS_PRESET,
  MEETING_SETTINGS_PRESET_LIST
} from '~/core/constants'
import { applyPreset } from '~/actions/room-settings'
import { getSelectedPreset, isLargePreset, isPublicPreset } from '~/selectors/room-settings'

const MeetingSettings = styled(Card)`
  margin: 24px;
  padding: 12px 32px 16px;
`

const MeetingSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-direction: row-reverse;
  justify-content: space-between;
`

const Container = styled.div`
  color: ${props => props.theme.color};
  height: 100%;
  width: 100%;

  @media (max-width: ${LARGE_LAYOUT_THRESHOLD}px) {
    height: 100svh;
  }
`

const Title = styled.h1`
  font-size: 24px;
`

const RoomName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SharingLink = styled(TextLink)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledCopyIcon = styled(CopyIcon)`
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.color};
  }
`

const PresetType = styled.div`
  margin: 16px 0;
`

const Settings = ({ room }) => {
  useWindowSize()
  const dispatch = useDispatch()
  const selectedPreset = useSelector(getSelectedPreset)
  const isLargeMeeting = useSelector(isLargePreset)
  const isPublicMeeting = useSelector(isPublicPreset)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    setIsReady(true)
  }, [])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(room)
    showInlineNotification(translate('meeting_info_copied'))
  }, [room])

  const handleAddPreset = useCallback((value) => {
    dispatch(applyPreset(value))
  }, [dispatch])

  if (!isReady) {
    return (
      <Container>
        <AppContainer withBackground>
          <SpinnerContainer><Spinner /></SpinnerContainer>
        </AppContainer>
      </Container>
    )
  }

  return (
    <Container>
      <AppContainer withBackground>
        <SessionWrapper>
          <MeetingSettings>
            <MeetingSettingsHeader>
              <BrandLogoIcon
                size={70}
                theme={{
                  viewBox: '0 0 140 63'
                }}
              />
              <Title>{translate('meeting_settings')}</Title>
            </MeetingSettingsHeader>
            <SettingsContainer noBorder>
              <StyledLegend>{translate('meeting_link')}</StyledLegend>
              <RoomName>
                <IconButton
                  aria-label={translate('copy_meeting_link')}
                  onClick={handleCopy}
                >
                  <StyledCopyIcon />
                </IconButton>
                <SharingLink href={room} target='_blank'>{room}</SharingLink>
              </RoomName>
            </SettingsContainer>
            <StyledLegend>{translate('select_meeting_type')}</StyledLegend>
            <Select
              alwaysDisplayPlaceholder
              inputId='meeting-select'
              onChange={handleAddPreset}
              closeMenuOnSelect
              options={MEETING_SETTINGS_PRESET_LIST}
              components={{
                Option: MeetingPresetOption,
                ClearIndicator: undefined
              }}
              testId='preset-selection'
              getOptionLabel={(option) => translate(option.label)}
              value={MEETING_SETTINGS_PRESET[selectedPreset]}
            />
            <PresetType>
              <PresetTitle>
                {`${translate('settings_for_meeting_type')} ${translate(MEETING_SETTINGS_PRESET[selectedPreset]?.labelPlural)}`}
              </PresetTitle>
              <SettingsDescription>{translate(MEETING_SETTINGS_PRESET[selectedPreset]?.description)}</SettingsDescription>
            </PresetType>
            <SettingsWrapper>
              {!isPublicMeeting && <AccessSettings />}
              {isLargeMeeting && <ActiveParticipantsSettings />}
              <ModerationSettings />
              <RecordingSettings />
              <SecuritySettings />
            </SettingsWrapper>
          </MeetingSettings>
        </SessionWrapper>
      </AppContainer>
      <DialogsRenderer />
    </Container>
  )
}

export default withNotificationsHoC(withThemeAndStoreHoC(Settings))
